/**
 * List of article URLs that are excluded from displaying the below-header ad
 * and have modified ad density rules applied for A/B testing.
 *
 * URLs in this list serve dual purposes:
 * 1. Exclude below-header ad: Pages matching these URLs will not display
 *    the below-header ad (checked in withTemplate.js)
 *
 * 2. Apply modified ad density rules: Articles/Reviews matching these URLs
 *    have alternative ad injection behavior (checked in Article.js and Review.js):
 *    - First mobile ad injected after ~3 folds (~100 words) instead of default
 *    - Ads injected before H2 tags for improved content flow
 *
 * Performance: Converted to Set for O(1) lookup time with 512 URLs.
 *
 * @type {Set<string>}
 * @see withTemplate.js - Below-header ad exclusion logic
 * @see Article.js - Ad density test rules application
 * @see Review.js - Ad density test rules application
 */
const excludeBelowHeaderAdUrls = [
  '/bmw/ix1',
  '/vauxhall/grandland',
  '/audi/a3',
  '/land-rover/range-rover',
  '/fiat/500',
  '/audi/q3',
  '/ford/capri',
  '/ford/puma',
  '/mercedes/a-class',
  '/peugeot/3008',
  '/land-rover/range-rover-evoque',
  '/toyota/yaris',
  '/volkswagen/golf',
  '/volvo/xc40',
  '/audi/q5',
  '/citroen/c3',
  '/ford/focus',
  '/ford/ranger',
  '/hyundai/i10',
  '/mercedes/gla',
  '/nissan/juke',
  '/skoda/kodiaq',
  '/vauxhall/mokka',
  '/dacia/duster',
  '/ineos/grenadier',
  '/mazda/cx-5',
  '/porsche/macan',
  '/seat/ibiza',
  '/tesla/model-3',
  '/toyota/rav4',
  '/audi/a4',
  '/audi/q7',
  '/bmw/3-series',
  '/bmw/x3',
  '/honda/cr-v',
  '/hyundai/kona',
  '/kia/ev3',
  '/kia/niro',
  '/peugeot/5008',
  '/renault/captur',
  '/skoda/fabia',
  '/volkswagen/t-roc',
  '/bmw/i4',
  '/citroen/c1',
  '/hyundai/i20',
  '/jeep/avenger',
  '/kia/ceed',
  '/kia/sorento',
  '/mazda/3',
  '/mercedes/cla',
  '/mg/zs',
  '/nissan/micra',
  '/peugeot/308',
  '/seat/arona',
  '/skoda/enyaq',
  '/skoda/octavia',
  '/volkswagen/transporter',
  '/bmw/ix',
  '/citroen/c4',
  '/fiat/panda',
  '/hyundai/ioniq-5',
  '/kia/stonic',
  '/mercedes/gle',
  '/mitsubishi/outlander',
  '/land-rover/range-rover-velar',
  '/skoda/kamiq',
  '/toyota/yaris-cross',
  '/volkswagen/id-buzz',
  '/volkswagen/touareg',
  '/byd/dolphin',
  '/lexus/lbx',
  '/mazda/cx-30',
  '/mercedes/eqb',
  '/polestar/3',
  '/skoda/superb',
  '/toyota/prius',
  '/volkswagen/id4',
  '/volkswagen/taigo',
  '/bmw/i7',
  '/bmw/x4',
  '/byd/atto-3',
  '/ford/galaxy',
  '/jaguar/i-pace',
  '/kia/rio',
  '/toyota/auris',
  '/volkswagen/passat',
  '/audi/e-tron-gt',
  '/byd/sealion-7',
  '/cupra/leon',
  '/hyundai/i30/n',
  '/jeep/compass',
  '/lexus/nx',
  '/lotus/emira',
  '/mg/mg4',
  '/nissan/navara',
  '/peugeot/108',
  '/renault/austral',
  '/renault/symbioz',
  '/toyota/bz4x',
  '/toyota/gt-86',
  '/alfa-romeo/giulietta',
  '/audi/q6-e-tron',
  '/bmw/ix2',
  '/ford/mustang-mach-e',
  '/hyundai/bayon',
  '/jaecoo/7',
  '/kia/soul',
  '/mini/clubman',
  '/polestar/4',
  '/toyota/c-hr',
  '/volvo/xc60',
  '/hyundai/kona/electric',
  '/byd/seal-u',
  '/mg/zs/ev',
  '/mercedes/eqc',
  '/mg/mg5',
  '/product-group-tests/95031/best-steering-wheel-locks',
  '/best/hybrid-suv',
  '/best/new-cars-coming-soon',
  '/product-group-tests/364285/best-wireless-apple-carplay-and-android-auto-adapters-2024',
  '/354113/best-ceramic-coatings',
  '/best/electric-suvs',
  '/product-group-tests/352913/best-obd-readers-2023',
  '/product-group-tests/63573/best-navigation-apps-2025',
  '/best/fastest-nurburgring-lap-record-times',
  '/exclusive/76760/best-car-headlight-bulbs-2024',
  '/product-group-tests/36236/best-battery-chargers-20232024',
  '/best/large-suvs',
  '/car-care/357935/best-car-drying-cloths-2024',
  '/best/fastest-0-60-car',
  '/product-group-tests/96130/best-pressure-washers',
  '/best/pick-up-trucks',
  '/best/seven-seat-hybrid-cars',
  '/product-group-tests/84791/best-torque-wrenches-2023',
  '/best/used-hybrid-cars',
  '/best/luxury-suvs',
  '/car-care/86280/best-scratch-removers-2024',
  '/product-group-tests/86069/best-car-foot-pumps',
  '/product-group-tests/89534/best-car-tyre-inflators-2023',
  '/product-group-tests/94099/best-rechargeable-torches-2024',
  '/product-group-tests/88304/best-dab-car-radio-adaptors',
  '/best/fastest-suv',
  '/best/4x4s-for-off-roading',
  '/product-group-tests/63765/best-car-upholstery-cleaners',
  '/product-group-tests/36198/best-boot-organisers-2025',
  '/product-group-tests/90576/best-rotary-multitools-2024',
  '/genesis/gv70',
  '/product-group-tests/65786/best-cordless-impact-wrench-2024',
  '/best/sports-cars',
  '/accessories-tyres/92369/best-theory-test-apps',
  '/car-care/362297/best-cordless-ratchet-2024',
  '/product-group-tests/352563/best-racing-games-android-and-ios-2023',
  '/best/small-automatic-cars',
  '/best/used-small-cars',
  '/mercedes/glc/97273/long-term-test-review-mercedes-glc-suv',
  '/best/diesel-cars',
  '/product-group-tests/106025/best-towbar-mounted-storage-boxes-and-racks-2024',
  '/best/classic-british-cars',
  '/best/small-hybrid-cars',
  '/best/hybrid-vans',
  '/product-group-tests/85240/best-car-wash-mitts',
  '/product-group-tests/36044/best-digital-multimeters-20242025',
  '/best/medium-suvs-uk-used',
  '/best/8-seater-car',
  '/car-care/106864/best-car-polish',
  '/best/small-suv-uk-used',
  '/accessories-tyres/108259/best-headlamp-restorers-2019',
  '/best/cool-cars-all-time',
  '/product-group-tests/84707/best-wheel-brushes-2020-group-test',
  '/best/ugliest-cars',
  '/volkswagen/golf/354949/new-volkswagen-golf-etsi-2021-review',
  '/subaru/solterra',
  '/mitsubishi/outlander/87981/long-term-test-review-mitsubishi-outlander-phev',
  '/toyota/yaris/358129/toyota-yaris-cross-design-long-term-test',
  '/best/safest-cars-uk',
  '/product-group-tests/60553/best-car-wheel-wax-and-sealant',
  '/best/land-rover-defender-alternatives',
  '/mercedes/eqe',
  '/product-group-tests/36214/best-12v-cool-boxes-2024',
  '/best/deals/best-electric-car-deals',
  '/product-group-tests/90998/best-tyre-shine-2021',
  '/best/deals/cars-with-free-insurance',
  '/product-group-tests/366104/best-dehumidifier-pouches-2025',
  '/renault/twizy',
  '/product-group-tests/64398/best-interior-trim-cleaners-2022',
  '/porsche/macan/electric',
  '/bmw/x3/366133/new-bmw-x3-m50-2025-review-quick-suv-cut-above-its-rivals',
  '/audi/a6/e-tron',
  '/best/used-convertibles',
  '/honda/cr-v/106134/suv/long-term',
  '/best/future-classics',
  '/product-group-tests/360451/best-pressure-washer-trigger-guns-2023',
  '/best/hot-hatch',
  '/product-group-tests/360285/best-folding-utility-knives-and-retractable-blades-2023',
  '/car-care/90880/best-car-wash-wax-sprays-2024',
  '/product-group-tests/360342/best-car-colour-restorers',
  '/hyundai/ioniq-5/365902/new-hyundai-ioniq-5-n-line-2025-review-upgrades-elevate-popular-ev-even',
  '/hyundai/ioniq-9',
  '/audi/a5/avant',
  '/genesis/gv80',
  '/best/cheapest-cars',
  '/best/people-carriers-and-mpvs',
  '/toyota/proace',
  '/bmw/ix/366109/new-bmw-ix-m70-review-great-ev-just-got-even-better',
  '/classic-cars/104954/jensen-interceptor-buying-guide-and-review-1966-1976',
  '/product-group-tests/90077/best-multitools-2025',
  '/alfa-romeo/tonale',
  '/mercedes/x-class',
  '/best/car-for-learner-drivers',
  '/genesis/gv60',
  '/product-group-tests/93312/best-fabric-protectors-2024',
  '/bmw/8-series',
  '/mazda/cx-60',
  '/best/cars-3-isofix-child-seat-points',
  '/product-group-tests/85474/best-tread-depth-gauges',
  '/product-group-tests/59283/best-ratchet-spanner-sets-2024',
  '/best/used-electric-cars',
  '/nissan/townstar',
  '/best/cars-for-tall-people',
  '/product-group-tests/360463/best-brake-fluid-testers-2023',
  '/subaru/xv',
  '/ssangyong/rexton',
  '/hyundai/veloster',
  '/product-group-tests/85922/best-car-interior-wipes-2023',
  '/ora/03/365782/new-gwm-ora-03-gt-2025-review-sporty-ev-supermini-isnt-cheap-wed-hope',
  '/nissan/leaf/104182/long-term-test-review-nissan-leaf-tekna',
  '/car-care/60400/best-coolant-testers-2022',
  '/volvo/v90/cross-country-estate',
  '/best/track-day-cars',
  '/mini/countryman/98567/suv/practicality',
  '/best/cars-for-long-distance-driving',
  '/citroen/c3-picasso',
  '/lexus/ux',
  '/suzuki/jimny/20398/1998-2018-4x4',
  '/best/mercedes-cars',
  '/best/vans-to-buy-second-hand',
  '/mitsubishi/shogun',
  '/product-group-tests/356313/best-folding-e-bikes-2024',
  '/nissan/nv200',
  '/product-group-tests/64983/best-spanner-sets-2022',
  '/maserati/ghibli',
  '/toyota/auris/hybrid',
  '/used-car-tests/362469/used-bmw-z3-1995-2002-review-modern-classic',
  '/suzuki/alto',
  '/best/volvo-xc40-alternatives',
  '/fiat/ducato-van/64676/van',
  '/mitsubishi/l200/98170/long-term-test-review-mitsubishi-l200',
  '/hyundai/kona/361262/hyundai-kona-electric-n-line-review-not-exciting-its-looks-suggest',
  '/product-group-tests/66728/best-head-torches-2022',
  '/best/ford-cars-of-all-time',
  '/kia/proceed/gt',
  '/best/0-finance-car-deals',
  '/best/car-infotainment-systems',
  '/lexus/nx/92118/long-term-test-review-lexus-nx-300h',
  '/peugeot/partner',
  '/lexus/lfa',
  '/best/japanese-kei-cars',
  '/best/small-vans',
  '/kia/niro/357816/new-kia-niro-hybrid-2022-review',
  '/bmw/2-series/gran-tourer',
  '/best/executive-cars',
  '/hyundai/ioniq/102999/long-term-test-review-hyundai-ioniq-plug-in-hybrid',
  '/skoda/rapid/spaceback',
  '/best/cheapest-hybrid-cars',
  '/skoda/superb/estate',
  '/renault/kangoo/van',
  '/mg/104950/mg-rv8-buying-guide-and-review-1992-1995',
  '/mg/mg5/62129/new-mg5-ev-2022-review',
  '/product-group-tests/355017/best-car-part-websites-2025',
  '/citroen/grand-c4-spacetourer',
  '/skoda/octavia/scout',
  '/citroen/e-c3',
  '/cupra/formentor/356131/cupra-formentor-v1-long-term-test',
  '/peugeot/bipper',
  '/best/hyundai-ioniq-5-alternatives',
  '/toyota/proace/verso',
  '/audi/a7/103916/long-term-test-review-audi-a7-sportback',
  '/best/quietest-cars-uk',
  '/toyota/iq/360157/used-toyota-iq-mk1-2009-2015-review',
  '/land-rover/discovery/used-land-rover-discovery-4-2009-2017-review',
  '/bmw/x3/356435/bmw-x3-2017-2024-xdrive30d-m-sport-review',
  '/product-group-tests/64350/best-leather-cleaners-2022',
  '/vauxhall/corsa/electric',
  '/alfa-romeo/mito',
  '/skoda/fabia/358407/skoda-fabia-10-tsi-long-term-test',
  '/volkswagen/id7/364017/new-volkswagen-id7-gtx-2024-review-vws-most-accomplished-ev-yet',
  '/nissan/juke/109175/nissan-juke-tekna-long-term-test',
  '/best/gt-cars',
  '/hyundai/i800',
  '/lexus/rx',
  '/volkswagen/golf/61003/used-volkswagen-golf-mk7-2013-2019-review',
  '/bmw/2-series/359265/new-bmw-2-series-230i-m-sport-review',
  '/ford/tourneo-connect',
  '/ford/fusion',
  '/product-group-tests/36201/best-usb-fast-chargers-2022',
  '/bmw/4-series/gran-coupe',
  '/vauxhall/grandland-x/104839/suv/practicality',
  '/porsche/cayman',
  '/hyundai/iload',
  '/best/large-panel-vans',
  '/hyundai/coupe/coupe',
  '/product-group-tests/356901/best-slot-car-racers-20212022',
  '/dfsk/ec35',
  '/smart/forfour',
  '/citroen/relay',
  '/ford/kuga/358040/used-ford-kuga-mk3-2020-date-review-popular-and-desirable-mid-size-suv',
  '/best/cheapest-cars-to-run-uk',
  '/bmw/2-series/convertible',
  '/bmw/ix3/353621/new-bmw-ix3-2021-review',
  '/skoda/citigo',
  '/mercedes/vito',
  '/peugeot/expert',
  '/audi/a6/105898/avant/practicality',
  '/ssangyong/rodius',
  '/bmw/2-series/358933/new-bmw-220d-m-sport-coupe-2022-review',
  '/subaru/forester/104321/new-subaru-forester-2023-review',
  '/suzuki/104883/suzuki-cappuccino-buying-guide-and-review-1991-1997',
  '/product-group-tests/91778/best-gopro-and-action-cameras-2024',
  '/renault/kadjar',
  '/alfa-romeo/stelvio',
  '/audi/a5/cabriolet',
  '/product-group-tests/36120/best-first-aid-kits-2024',
  '/audi/a6/avant',
  '/kia/sorento/362795/new-kia-sorento-2024-review-good-car-just-got-even-better',
  '/nissan/qashqai/356843/nissan-qashqai-tekna-long-term-test',
  '/best/most-economical-suvs',
  '/bmw/x2/362557/new-bmw-ix2-xdrive30-m-sport-review-sharp-coupe-suv',
  '/peugeot/308/356014/new-peugeot-308-2022-review',
  '/fiat/ducato',
  '/best/used-estate-cars',
  '/lotus/europa/coupe',
  '/leapmotor/t03',
  '/ssangyong/korando/20585/suv',
  '/best/cars-under-2k',
  '/kia/ev6/359206/new-kia-ev6-air-2022-review',
  '/land-rover/discovery-sport/94555/long-term-test-review-land-rover-discovery-sport',
  '/mazda/3/mps',
  '/best/cheapest-vans',
  '/bmw/6-series/m6-gran-coupe',
  '/skoda/fabia/scout',
  '/best/used-mpvs-people-carriers',
  '/volkswagen/passat/alltrack',
  '/fiat/tipo',
  '/audi/s6',
  '/best/bmw-cars',
  '/product-group-tests/360823/best-dog-smell-removers-cars-air-fresheners-eliminate-wet-dog-odour',
  '/best/supercars',
  '/infiniti/q30',
  '/kgm/tivoli',
  '/genesis/g80',
  '/nissan/104976/nissan-stagea-buying-guide-and-review-1996-2007',
  '/vauxhall/mokka-x',
  '/dacia/logan',
  '/mercedes/e-class/cabriolet',
  '/infiniti/qx70',
  '/best/cars-for-snow',
  '/honda/cr-z',
  '/renault/zoe',
  '/best/most-aerodynamic-cars',
  '/smart/1',
  '/product-group-tests/103227/best-kneeler-mats-and-pads-2024',
  '/ineos/grenadier/quartermaster',
  '/subaru/crosstrek',
  '/ford/fiesta/102485/long-term-test-review-ford-fiesta',
  '/mini/cooper/convertible',
  '/honda/civic/355557/used-honda-civic-review/used-honda-civic-mk9-review',
  '/best/electric-cars-coming-soon',
  '/volkswagen/id7/tourer',
  '/hyundai/i10/96370/used-hyundai-i10-mk2-2014-2019-review',
  '/best/crossover-cars',
  '/best/used-sport-cars',
  '/vauxhall/astra/86774/used-vauxhall-astra-review',
  '/fiat/500/convertible',
  '/best/cheapest-cars-to-insure-uk',
  '/hyundai/i20/20499/2008-2014-hatchback',
  '/product-group-tests/351913/best-ski-carriers-2020',
  '/best/small-4x4',
  '/best/toyota-cars-of-all-time',
  '/toyota/auris/20651/2007-2012-hatchback',
  '/best/kia-sportage-alternatives',
  '/mercedes/s-class/coupe',
  '/hyundai/bayon/354734/new-hyundai-bayon-2021-review',
  '/mercedes/r-class',
  '/renault/wind/convertible',
  '/accessories-tyres/36158/best-car-seat-covers-and-protectors-2018',
  '/best/saloon-cars',
  '/mercedes/c-class/estate',
  '/best/road-trip-cars',
  '/toyota/proace-max',
  '/skoda/octavia/63848/estate',
  '/mazda/mx-5',
  '/citroen/c4/357436/new-citroen-c4-puretech-155-2022-review',
  '/fiat/talento',
  '/audi/a5/sportback',
  '/honda/cr-v/360347/used-honda-cr-v-mk5-2019-date-review',
  '/best/used-cars-under-10k',
  '/cupra/leon/358146/cupra-leon-245-vz1-review',
  '/honda/cr-v/36830/used-honda-cr-v-mk3-review',
  '/accessories-tyres/106406/best-rain-repellent-screen-wash-2019',
  '/bmw/104942/bmw-z3-buying-guide-and-review-1996-2002',
  '/ford/focus/estate',
  '/toyota/iq',
  '/toyota/hilux/64559/van',
  '/suzuki/swift/20396/2010-2017-hatchback',
  '/best/performance-cars',
  '/ford/transit-courier/87115/van',
  '/ds/9',
  '/product-group-tests/86454/best-car-shampoo-2021',
  '/nissan/qashqai/20602/4x4',
  '/volvo/ec40',
  '/best/slowest-cars',
  '/best/cars-with-big-boots',
  '/mitsubishi/outlander/phev',
  '/citroen/berlingo/20441/used-citroen-berlingo-multispace-mk2-2008-2018-review',
  '/volkswagen/caddy-maxi/mpv',
  '/land-rover/range-rover-sport/sv',
  '/depth-reviews/358525/toyota-corolla-commercial-review',
  '/vauxhall/corsa/61015/used-vauxhall-corsa-mk4-2014-2020-review',
  '/maserati/quattroporte',
  '/audi/a3/cabriolet',
  '/alfa-romeo/159/96983/used-alfa-romeo-159-review',
  '/mazda/3/357891/used-mazda-3-mk-3-2014-2019-review',
  '/classic-cars/104886/austin-healey-frogeye-sprite-buying-guide-1958-1961',
  '/kia/sportage/94306/suv/practicality',
  '/volkswagen/crafter',
  '/bmw/4-series/m4-convertible',
  '/fiat/500/l',
  '/best/volkswagen-golf-alternatives',
  '/peugeot/boxer',
  '/nissan/navara/64637/van',
  '/best/audi-cars-of-all-time',
  '/mercedes/e-class/coupe',
  '/volkswagen/tiguan/354032/used-volkswagen-tiguan-review',
  '/kia/ev6/365051/used-kia-ev6-mk1-2021-date-buyers-guide-classy-ev-superb-family-car',
  '/mercedes/class/352227/new-mercedes-220-d-2020-review',
  '/mercedes/cls-class/20586/saloon',
  '/product-group-tests/354029/best-car-headrest-tablet-holders-2021',
  '/renault/megane/355784/new-renault-megane-e-tech-phev-2021-review',
  '/honda/civic/tourer',
  '/best/company-cars/ps50000-ps60000',
  '/peugeot/partner-tepee',
  '/audi/a6/allroad',
  '/mazda/6',
  '/product-group-tests/36134/best-suv-tyres',
  '/mazda/6/estate',
  '/nissan/micra/361295/used-nissan-micra-mk4-2010-2016-review',
  '/classic-cars/104931/daimler-dart-buying-guide-and-review-1959-1964',
  '/skywell/be11',
  '/mazda/mx-30',
  '/citroen/dispatch',
  '/volkswagen/scirocco/104110/used-volkswagen-scirocco-review',
  '/volkswagen/golf/estate',
  '/isuzu/d-max',
  '/kia/picanto/20506/2011-2017-hatchback',
  '/kia/sportage/20463/2010-2016-4x4',
  '/best/used-cars-under-3k',
  '/maserati/ghibli/108706/used-maserati-ghibli-review',
  '/classic-cars/104923/bond-bug-buying-guide-and-review-1970-1974',
  '/volvo/v70',
  '/kia/picanto/20927/2005-2011-hatchback',
  '/best/highest-horsepower-cars',
  '/smart/3',
  '/ford/kuga/20516/2008-2012-4x4',
  '/vauxhall/corsa/359294/used-vauxhall-corsa-mk5-2020-date-review',
  '/classic-cars/104927/triumph-vitesse-buying-guide-and-review-1962-1971',
  '/audi/a1/95744/used-audi-a1-mk1-2010-2018-review',
  '/land-rover/range-rover-sport/64484/suv/practicality',
  '/volkswagen/id3/354888/new-volkswagen-id3-tour-pro-s-2021-review',
  '/fiat/scudo',
  '/skoda/octavia/351616/used-skoda-octavia-mk2-2004-2013-review',
  '/kia/proceed',
  '/volvo/xc70',
  '/citroen/c1/20379/2007-2014-hatchback',
  '/mercedes/class/class-a35-amg',
  '/porsche/cayenne/59848/2011-2017-4x4',
  '/skoda/superb/92123/estate',
  '/product-reviews/65265/best-car-screen-wash-2021',
  '/hyundai/i10/20498/hatchback',
  '/volkswagen/tiguan/95358/4x4/practicality',
  '/accessories-tyres/108270/best-water-bottles-2019',
  '/volkswagen/tiguan/98865/used-volkswagen-tiguan-mk1-2008-2016-review',
  '/volkswagen/fox/360136/used-volkswagen-fox-2006-2012-review',
  '/mercedes/gls',
  '/skoda/rapid',
  '/volkswagen/passat/estate',
  '/accessories-tyres/92640/best-spray-wax-detailer-2018',
  '/audi/a4/allroad',
  '/vauxhall/vivaro/87593/2014-2019-van',
  '/volkswagen/e-transporter',
  '/classic-cars/104981/rover-sd1-buying-guide-and-review-1976-1987',
  '/porsche/104987/porsche-928-buying-guide-and-review-1977-1995',
  '/best/cheap-convertibles',
  '/saab/104963/saab-96-buying-guide-and-review-1960-1980',
  '/suzuki/swift/362261/used-suzuki-swift-mk7-2017-2024-review-fun-pricey-supermini',
  '/mercedes/eqc/363767/used-mercedes-eqc-mk1-2019-2024-buyers-guide-premium-ev-has-its-compromises',
  '/hyundai/i40',
  '/hyundai/ioniq-5/359595/new-hyundai-ioniq-5-namsan-edition-2023-review',
  '/skoda/kodiaq/353680/used-skoda-kodiaq-mk1-2017-2024-review',
  '/mg/zs/105749/suv',
  '/mazda/2/354290/used-mazda-2-review/used-mazda-2-mk2-review',
  '/classic-cars/104948/triumph-tr4-buying-guide-and-review-1961-1967',
  '/land-rover/range-rover-evoque/95469/used-range-rover-evoque-mk1-2011-2018-review',
  '/vauxhall/insignia/hatchback',
  '/toyota/aygo/60466/used-toyota-aygo-review',
  '/exclusive/366020/best-car-voice-control-systems-tested-and-rated',
  '/best/cars-modify',
  '/vauxhall/crossland/353794/used-vauxhall-crossland-x-review',
  '/skoda/kodiaq/101915/long-term-test-review-skoda-kodiaq-se-l-20-tdi',
  '/jaguar/f-pace/352471/used-jaguar-f-pace-review',
  '/vauxhall/astra/sports-tourer',
  '/nissan/qashqai/86843/used-nissan-qashqai-mk2-2014-2020-review/used-nissan-qashqai-mk1-2007-2013-review',
  '/mazda/2/99708/mazda-2-vs-ford-fiesta',
];

export default new Set(excludeBelowHeaderAdUrls);
